import { initLogRocket } from 'monitorToolsSetup'
import usePrevious from '@shared/hooks/use-previous'
import { onlineManager } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { type NavigateOptions, type To, useNavigate, useParams } from 'router-v6'
import { REGIONS } from 'v2/configs/constants'
import { queryClient } from 'v2/configs/rq-setup'
import { parseConversationURL, updateUnreadCounts } from 'v2/pages/conversations/utils'
import { isTemplateChunk, isTextChunk } from 'v2/pages/conversations/utils/typeguards'
import { useCurrentStatusQuery } from 'v2/pages/settings/org-status/queries'
import { useBuyNumberStore } from 'v2/pages/settings/phone-numbers/store'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { toEmbedTo } from 'v2/shared/hooks/use-app-navigate'
import { useAppStore } from 'v2/state/app'
import { useSharedConversationStore } from 'v2/state/conversation'
import { usePhoneStore } from 'v2/state/phone'
import { WorkerMessageTypes } from 'v2/types'
import type { Chunk } from 'v2/types/chunk.internal'
import { getLocalAuth } from 'v2/utils'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { logger, setLoggerTags } from 'v2/utils/logger'
import { settingsService } from '../services/settings-service'
import { browserNotificationManager } from '../utils/browser-notification-manager'
import { primaryTabManager } from '../utils/primary-tab-manager'
import { conversationQueryKeys } from '../utils/query-key-management'
import { tabClosureManager } from '../utils/tab-closure-manager'
import { useAuth } from './use-auth'
import { useIdentifyAnalytics } from './use-identify-analytics'
import { useIntercom } from './use-intercom'

export const useAppEffects = () => {
  const { setAgentStatus, agentStatus, isHydrated } = useAppStore()
  const workerRef = useRef<SharedWorker | null>(null)
  const { data: agentStatusData } = useCurrentStatusQuery()
  const { data: profileData } = useProfileQuery()
  const humanSpecialistEnabled = profileData?.organizations?.[0]?.human_specialist_enabled ?? false
  const { userAuthenticated, logout } = useAuth()
  const { conversationId } = useParams()
  const prevConversationId = usePrevious(conversationId)
  const setMeta = useSharedConversationStore((state) => state.setMeta)
  const setLastReadTimestamp = useSharedConversationStore((state) => state.setLastReadTimestamp)
  const navigate = useNavigate()

  useIdentifyAnalytics()
  useIntercom()

  // Set the last read timestamp for the conversation when the conversationId changes
  useEffect(() => {
    if (prevConversationId) setLastReadTimestamp(prevConversationId, Date.now())
    if (conversationId) setLastReadTimestamp(conversationId, Date.now())
  }, [conversationId])

  useEffect(() => {
    if (agentStatus.updateType !== 'manual' && isHydrated) {
      settingsService
        .loginEvent()
        .then((data) => {
          setAgentStatus({ uuid: data?.data?.uuid, updateType: 'initial' })
        })
        .catch((err) => {
          logger.error('Error in loginEvent:', err)
        })
    }
  }, [isHydrated])

  useEffect(() => {
    const unbinds = [
      onlineManager.subscribe((isOnline) => {
        useAppStore.setState({ isOnline })
      }),

      // Invalidate the active conversation query when the app is active
      primaryEventBus.on(PrimaryEvents.APP_ACTIVE, () => {
        queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
      }),

      // Subscribe to query cache events to update unread counts
      queryClient
        .getQueryCache()
        .subscribe((event) => {
          // Only update unread counts if the app is focused on the primary tab
          if (
            event.type === 'updated' &&
            event.query.queryKey[0] === 'chunks' &&
            event.action.type === 'success'
          ) {
            const conversationNumber = event.query.queryKey[1]
            const { data } = event.query.state

            // Check if data exists, is in the correct format, and if the app is not focused on the current conversation
            // Feature: Unread count management
            if (
              data &&
              Array.isArray(data.pages) &&
              (document.hidden || conversationNumber !== parseConversationURL().conversationNumber)
            ) {
              const chunks = data.pages.flat()
              // Update unread counts for the conversation
              updateUnreadCounts(conversationNumber, chunks)
            }
            if (data?.pages?.length) {
              const chunks = data.pages.flat() as Chunk[]
              const isTextOrTemplateChunk = (c: Chunk) => isTextChunk(c) || isTemplateChunk(c)
              const failedChunk = chunks.find(
                (c) =>
                  isTextOrTemplateChunk(c) &&
                  c.meta.channel_meta?.error_data?.errorType === 'Whole conversation',
              )
              if (failedChunk && isTextOrTemplateChunk(failedChunk)) {
                setMeta(conversationNumber, {
                  error: {
                    type: failedChunk.meta.channel_meta?.error_data?.errorType as
                      | 'Whole conversation'
                      | 'Specific message',
                    message: failedChunk.meta.channel_meta?.error_data?.comments,
                    channel: failedChunk.channel,
                  },
                })
              }
            }
          }
        }),

      // Stop the worker when the user logs out
      primaryEventBus.on(PrimaryEvents.LOGOUT, async () => {
        if (workerRef.current) {
          workerRef.current.port.postMessage({ type: WorkerMessageTypes.stop })
          workerRef.current.port.close()
        }
      }),

      primaryEventBus.on(PrimaryEvents.APP_FOCUSED, (isFocused) => {
        const { conversationNumber } = parseConversationURL()
        if (conversationNumber && isFocused) {
          logger.debug('clearing unread counts', conversationNumber)
          useSharedConversationStore.getState().clearUnreadCounts(conversationNumber)
        }
      }),
    ]

    // Set the navigate function on the window object. Wrapped ONCE here with
    // toEmbedTo (identity outside /embed, idempotent) so every
    // window.cx.navigate caller passes bare console paths and can never
    // escape the embed iframe to the console login (AGENTS.md rule 18).
    window.cx.navigate = (to: To | number, options?: NavigateOptions) => {
      if (typeof to === 'number') {
        navigate(to)
        return
      }
      navigate(toEmbedTo(to), options)
    }

    // Cleanup function to close the worker port and unsubscribe from query cache events
    return () => {
      if (workerRef.current) workerRef.current.port.close()
      unbinds.forEach((unbind) => unbind())
    }
  }, [])

  // The shared worker drives agent heartbeat ping (every 10s). Only
  // human-specialist orgs need it — keep the worker dormant otherwise.
  useEffect(() => {
    if (!humanSpecialistEnabled) return

    const isSharedWorkerSupported = typeof SharedWorker !== 'undefined'
    if (!isSharedWorkerSupported || workerRef.current) return

    workerRef.current = new SharedWorker(
      // @ts-expect-error - treats this as commonjs
      new URL('../workers/shared-worker.ts', import.meta.url),
      {
        type: 'module',
        name: 'cx-shared-worker',
      },
    )
    // Start the worker's port
    workerRef.current.port.start()

    // Send authentication details to the worker
    const authDetails = getLocalAuth()
    workerRef.current.port.postMessage({
      type: WorkerMessageTypes.setAuth,
      data: { auth: authDetails },
    })

    // Start the worker
    workerRef.current.port.postMessage({ type: WorkerMessageTypes.start })
    queueMicrotask(() => {
      if (workerRef.current) {
        workerRef.current.port.onmessage = (event) => {
          if (event.data.type === WorkerMessageTypes.logout) logout()
        }
      }
    })
  }, [humanSpecialistEnabled, logout])

  // Initialize the primary tab manager and browser notification manager and reset the phone store if there is no active call
  // This effect is run on every login or reload
  useEffect(() => {
    if (userAuthenticated) {
      initLogRocket()
      // Reset the phone store if there is no active call
      if (!usePhoneStore.getState().activeCall) usePhoneStore.getState().reset()

      const user = getLocalAuth()
      // Set the logger tags for the user
      setLoggerTags({
        email: user?.email,
        roles: user?.roles?.join(',') || '',
        orgName: user?.organization_name,
        aomId: user?.aom_uuid,
        name: user?.name || '',
        region: user?.region || '',
      })

      useBuyNumberStore.getState().setCountry(user.region === REGIONS.IN ? 'IN' : 'US')

      primaryTabManager.init()
      // Initialize tab closure manager with the existing worker
      if (workerRef.current) {
        tabClosureManager.init(workerRef.current)
      }
      browserNotificationManager.init()
    } else {
      workerRef.current?.port.postMessage({ type: WorkerMessageTypes.stop })
    }
  }, [userAuthenticated])

  // Start or stop the worker based on the agent status
  useEffect(() => {
    if (!humanSpecialistEnabled) return
    if (agentStatusData) {
      if (!agentStatusData.availability) {
        workerRef.current?.port.postMessage({ type: WorkerMessageTypes.stop })
      } else {
        workerRef.current?.port.postMessage({ type: WorkerMessageTypes.start })
      }
    }
  }, [agentStatusData, humanSpecialistEnabled])
}
